import { FormFieldWrapper } from '@/components/forms/FormFieldWrapper';
import { Checkbox } from '@/components/ui/checkbox';
import { FormField } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Shield } from 'lucide-react';
import { Control } from 'react-hook-form';
import type { FormAData } from '../types/formA.types';

interface OriginOfAssetsSectionAProps {
  control: Control<FormAData>;
  showOtherField: boolean;
  originOfAssetsOptions: Array<{ value: string; label: string }>;
  tForms: (key: string) => string;
}

/**
 * OriginOfAssetsSectionA component (Form A specific)
 * Displays the origin of assets selection with checkboxes and optional "other" field
 */
export const OriginOfAssetsSectionA = ({
  control,
  showOtherField,
  originOfAssetsOptions,
  tForms,
}: OriginOfAssetsSectionAProps) => {
  return (
    <section className='space-y-6' aria-labelledby='origin-of-assets-heading-a'>
      <div className='flex items-center gap-2 mb-4'>
        <Shield className='w-5 h-5' aria-hidden='true' />
        <h3 id='origin-of-assets-heading-a' className='text-lg font-semibold'>
          {tForms('formK.sections.originOfAssets')}
        </h3>
      </div>
      <div className='space-y-4'>
        <FormField
          control={control}
          name='originOfAssets'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('formK.fields.originOfAssets')}
              name='originOfAssets'
              tooltip={tForms('formK.tooltips.originOfAssets')}
              error={fieldState.error?.message}
              isGroupLabel={true}
            >
              <div className='space-y-3'>
                {originOfAssetsOptions.map(option => (
                  <div
                    key={option.value}
                    className='flex items-center space-x-2'
                  >
                    <Checkbox
                      id={`origin-a-${option.value}`}
                      checked={field.value?.includes(option.value) || false}
                      onCheckedChange={checked => {
                        const currentValue = field.value || [];
                        if (checked) {
                          // Add to selection
                          field.onChange([...currentValue, option.value]);
                        } else {
                          // Remove from selection
                          field.onChange(
                            currentValue.filter(
                              (v: string) => v !== option.value
                            )
                          );
                        }
                      }}
                    />
                    <label
                      htmlFor={`origin-a-${option.value}`}
                      className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </FormFieldWrapper>
          )}
        />

        {/* Conditional "Other" text field */}
        {showOtherField && (
          <FormField
            control={control}
            name='originOfAssetsOther'
            render={({ field, fieldState }) => (
              <FormFieldWrapper
                label={tForms('formK.fields.originOfAssetsOther')}
                name='originOfAssetsOther'
                tooltip={tForms('formK.tooltips.originOfAssetsOther')}
                error={fieldState.error?.message}
                required
              >
                <Textarea
                  {...field}
                  placeholder={tForms('formK.placeholders.originOfAssetsOther')}
                  className='min-h-[100px] resize-y'
                  rows={3}
                />
              </FormFieldWrapper>
            )}
          />
        )}
      </div>
    </section>
  );
};
